import "./style.scss";

import { FormGroup } from "@mui/material";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import IosSwitch from "components/shared/IosSwitch";
import MessageBlock from "components/shared/MessageBlock";
import { Spinner } from "components/shared/Spinner";
import StyledButton from "components/shared/StyledButton";
import { whiteButton } from "components/TitlesList";
import { savePaymentFetchType } from "containers/DeclarationPage/utils";
import { electronicFormatIBAN, isValidBIC, isValidIBAN } from "ibantools";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { validateAdvanceBankInfos } from "store/advancePaymentSlice";
import { fetchBeneficiar, updateBeneficiary } from "store/beneficiaryStore";
import { validateBeneficiariesBankInfo } from "store/managementStore";
import { fetchPaymentsBetweenDates } from "store/paymentSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { useCompanySettings } from "hooks/useCompanySettings";
import { deriveIbanModalMode } from "helpers/paymentExportUtils";
import type { BankingFieldKey, CountryFeatures, InternationalBankingFieldKey } from "types/countryFeatures";

type BankingMode = "local" | "international";

type IBANForm = Partial<Record<BankingFieldKey | InternationalBankingFieldKey, string>> & {
    ribOwnerName: string;
    swift?: string;
    bypassIbanValidation?: boolean;
};

interface IBANProps {
    close: () => void;
    beneficiaryId: number | undefined;
    setPayments?: (payments: any) => void;
    confirmBankInfos?: (beneficiaryId: number, iban: string) => void;
}

const getPlaceholder = (
    fieldKey: BankingFieldKey,
    bankingFieldRules: Record<string, { length?: number; minLength?: number; maxLength?: number }> | undefined,
): string => {
    const rules = bankingFieldRules?.[fieldKey];
    if (!rules) return "";
    return "0".repeat(rules.length ?? rules.maxLength ?? 0);
};

// ─── Field section sub-components ────────────────────────────────────────────

const InternationalFields = ({ control, errors }: { control: any; errors: any }) => {
    const [t] = useTranslation();
    return (
        <>
            <TextFormComponent
                label="BIC / SWIFT"
                control={control}
                controlName="intl_swift"
                placeholder="Ex: CHASUS33"
                required
                customRequiredMessage={t("IbanModal.RequiredField")}
                error={errors.intl_swift as any}
            />
            <TextFormComponent
                label={t("IbanModal.IntlAccountNumber")}
                control={control}
                controlName="intl_account_number"
                placeholder="Ex: 123456789"
                required
                customRequiredMessage={t("IbanModal.RequiredField")}
                error={errors.intl_account_number as any}
            />
            <TextFormComponent
                label={t("IbanModal.IntlRoutingCode")}
                control={control}
                controlName="intl_routing_code"
                placeholder="Ex: 021000021"
                error={errors.intl_routing_code as any}
            />
            <TextFormComponent
                label={t("IbanModal.IntlBankName")}
                control={control}
                controlName="intl_bank_name"
                placeholder="Ex: JPMorgan Chase"
                error={errors.intl_bank_name as any}
            />
        </>
    );
};

const IbanFields = ({ control, errors, IBANValue }: { control: any; errors: any; IBANValue: string }) => {
    const [t] = useTranslation();
    return (
        <>
            <TextFormComponent
                label={t("IbanModal.IBAN")}
                control={control}
                controlName="iban"
                placeholder="Ex: XXX XXX XXX XXX XXX XXX"
                required
                customRequiredMessage={t("IbanModal.RequiredField")}
                error={errors.iban}
            />
            <TextFormComponent
                label={t("IbanModal.Swift")}
                control={control}
                controlName="swift"
                placeholder="Ex: SOGEFRPP"
                required={IBANValue.toUpperCase().slice(0, 2) !== "FR"}
                customRequiredMessage={t("IbanModal.RequiredField")}
                error={errors.swift}
            />
        </>
    );
};

const MetadataFields = ({
    control,
    errors,
    metadataFields,
    validationRules,
}: {
    control: any;
    errors: any;
    metadataFields: BankingFieldKey[];
    validationRules: CountryFeatures["validation"];
}) => {
    const [t] = useTranslation();
    return (
        <>
            {metadataFields.map((fieldKey) => {
                const rule = validationRules.bankingFields?.[fieldKey as keyof typeof validationRules.bankingFields] as
                    | { length?: number; minLength?: number; maxLength?: number }
                    | undefined;

                const validate = rule
                    ? (v: string) => {
                          if (!v) return true;
                          const clean = v.replace(/[\s-]/g, "");
                          if (rule.length != null) return new RegExp(`^\\d{${rule.length}}$`).test(clean);
                          if (rule.minLength != null)
                              return new RegExp(`^\\d{${rule.minLength},${rule.maxLength}}$`).test(clean);
                          return true;
                      }
                    : undefined;

                const validateMsg = rule
                    ? rule.length != null
                        ? t("Settings.BankFieldExactLength", {
                              field: t(`Beneficiary.${fieldKey}`),
                              count: rule.length,
                          })
                        : t("Settings.BankFieldLengthRange", {
                              field: t(`Beneficiary.${fieldKey}`),
                              min: rule.minLength,
                              max: rule.maxLength,
                          })
                    : undefined;

                return (
                    <TextFormComponent
                        key={fieldKey}
                        label={t(`Beneficiary.${fieldKey}`)}
                        control={control}
                        controlName={fieldKey}
                        placeholder={getPlaceholder(fieldKey, validationRules.bankingFields as any)}
                        required
                        customRequiredMessage={t("IbanModal.RequiredField")}
                        error={errors[fieldKey as keyof IBANForm] as any}
                        customValidate={validate}
                        customValidateMessage={validateMsg}
                    />
                );
            })}
        </>
    );
};

// ─── Main ─────────────────────────────────────────────────────────────────────

const IbanModal = ({ close, beneficiaryId, setPayments, confirmBankInfos }: IBANProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { countryFeatures, internationalBankingEnabled, companyInfos, companyCountryCode } = useCompanySettings();
    const { beneficiar, beneficiaryStatus } = useAppSelector((state) => state.beneficiary);
    const { periodsDateRange } = useAppSelector((state) => state.period);
    const { oldestArticlePaidDate } = useAppSelector((state) => state.article);
    const { payments } = useAppSelector((state) => state.payment);
    const [bankingMode, setBankingMode] = useState<BankingMode>("local");
    const [showByPassIbanMessage, setShowByPassIbanMessage] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const paymentPageData = JSON.parse(sessionStorage.getItem("payment_page_data") ?? "{}");

    const usesIban = countryFeatures.bankingFields.includes("iban");
    const metadataFields = countryFeatures.bankingFields.filter(
        (f): f is BankingFieldKey => f !== "iban" && f !== "bic",
    );

    const defaultValues: IBANForm = {
        ribOwnerName: "",
        iban: "",
        swift: "",
        bypassIbanValidation: false,
        intl_swift: "",
        intl_account_number: "",
        intl_routing_code: "",
        intl_bank_name: "",
        ...Object.fromEntries(metadataFields.map((f) => [f, ""])),
    };

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors },
    } = useForm({ defaultValues });
    const IBANValue = watch("iban") ?? "";

    useEffect(() => {
        beneficiaryId && dispatch(fetchBeneficiar(beneficiaryId));
    }, [beneficiaryId]);

    useEffect(() => {
        if (beneficiaryId === beneficiar?.id) {
            const meta = (beneficiar.bankingMetadata ?? {}) as Record<string, string>;
            setBankingMode(
                deriveIbanModalMode(
                    beneficiar,
                    internationalBankingEnabled,
                    companyCountryCode,
                    countryFeatures.bankingStatusRules,
                ),
            );
            reset({ ...beneficiar, ...meta });
        }
    }, [beneficiar]);

    // Les champs intl_* sont montés APRÈS que bankingMode passe à "international".
    // reset() appelé avant leur montage ne les peuple pas — on force setValue ici.
    useEffect(() => {
        if (bankingMode === "international" && beneficiar && beneficiaryId === beneficiar.id) {
            const meta = (beneficiar.bankingMetadata ?? {}) as Record<string, string>;
            setValue("intl_swift", meta.intl_swift ?? "");
            setValue("intl_account_number", meta.intl_account_number ?? "");
            setValue("intl_routing_code", meta.intl_routing_code ?? "");
            setValue("intl_bank_name", meta.intl_bank_name ?? "");
        }
    }, [bankingMode]);

    const handleConfirmBankInfos = () => {
        if (!beneficiaryId) return;
        dispatch(validateBeneficiariesBankInfo({ id: beneficiaryId }));
        dispatch(validateAdvanceBankInfos({ id: beneficiaryId }));
        confirmBankInfos && confirmBankInfos(beneficiaryId, IBANValue);
    };

    const onSubmit = async (data: IBANForm) => {
        let req: Record<string, any>;

        const existingMeta = (beneficiar?.bankingMetadata ?? {}) as Record<string, string>;
        const bankAccountCountryField = existingMeta.bank_account_country
            ? { bank_account_country: existingMeta.bank_account_country }
            : {};

        if (bankingMode === "international") {
            req = {
                ribOwnerName: data.ribOwnerName,
                bankingMetadata: {
                    ...bankAccountCountryField,
                    intl_swift: data.intl_swift ?? "",
                    intl_account_number: data.intl_account_number ?? "",
                    ...(data.intl_routing_code && { intl_routing_code: data.intl_routing_code }),
                    ...(data.intl_bank_name && { intl_bank_name: data.intl_bank_name }),
                },
                acceptedPayments: "rib",
                validBankInfo: 1,
            };
        } else if (usesIban) {
            const iban = electronicFormatIBAN(data.iban ?? "");
            const isFrenchIban = iban?.toUpperCase().startsWith("FR") ?? false;
            const bicVal = (data.swift ?? "").replace(/ /g, "");
            const ibanValid = isValidIBAN(iban!);
            const bicValid = isFrenchIban ? !bicVal || isValidBIC(bicVal) : !!bicVal && isValidBIC(bicVal);
            if (!ibanValid || !bicValid) {
                if (!data.bypassIbanValidation) {
                    setShowByPassIbanMessage(true);
                    return;
                }
            }
            req = {
                ribOwnerName: data.ribOwnerName,
                iban,
                swift: data.swift,
                acceptedPayments: "rib",
                validBankInfo: 1,
                ...(data.bypassIbanValidation && { bypassIbanValidation: true }),
            };
        } else {
            req = {
                ribOwnerName: data.ribOwnerName,
                bankingMetadata: {
                    ...bankAccountCountryField,
                    ...Object.fromEntries(metadataFields.map((f) => [f, data[f] ?? ""]).filter(([, v]) => v !== "")),
                },
                acceptedPayments: "rib",
                validBankInfo: 1,
            };
        }

        try {
            setIsLoading(true);
            const response = await dispatch(updateBeneficiary({ data: req, id: beneficiaryId ?? -1 }));
            if (response.meta.requestStatus === "fulfilled") {
                dispatch(
                    fetchPaymentsBetweenDates({
                        startDate:
                            paymentPageData.dateRange?.startDate || periodsDateRange.startDate || oldestArticlePaidDate,
                        endDate: paymentPageData.dateRange?.endDate || periodsDateRange.endDate || new Date(),
                    }),
                );
                savePaymentFetchType("payments");
                handleConfirmBankInfos();
                setPayments && setPayments(payments);
                close();
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="iban-form-container">
            {beneficiaryStatus === "pending" ? (
                <div className="iban-spinner">
                    <Spinner />
                </div>
            ) : (
                <form className="iban-form">
                    <TextFormComponent
                        label={t("IbanModal.NameOwner")}
                        control={control}
                        controlName="ribOwnerName"
                        placeholder={t("IbanModal.NameExample")}
                        customRequiredMessage={t("IbanModal.RequiredField")}
                        error={errors.ribOwnerName}
                    />
                    {bankingMode === "international" ? (
                        <InternationalFields control={control} errors={errors} />
                    ) : usesIban ? (
                        <IbanFields control={control} errors={errors} IBANValue={IBANValue} />
                    ) : (
                        <MetadataFields
                            control={control}
                            errors={errors}
                            metadataFields={metadataFields}
                            validationRules={countryFeatures.validation}
                        />
                    )}
                </form>
            )}
            {usesIban && bankingMode === "local" && showByPassIbanMessage && (
                <div className="iban-bypass-container">
                    <MessageBlock type="warning" text={t("IbanModal.IbanError")} style={{ marginBlock: "10px" }} />
                    <Controller
                        name="bypassIbanValidation"
                        control={control}
                        render={({ field }) => (
                            <FormGroup
                                sx={{
                                    width: "100%",
                                    flexDirection: "row",
                                    justifyContent: "space-between",
                                    flexWrap: "nowrap",
                                    fontSize: "13px",
                                    gap: "8px",
                                    marginTop: "8px",
                                    alignItems: "center",
                                }}
                                {...field}
                            >
                                <div className="iban-switch-label">
                                    <div>
                                        <span className="iban-optional-mark">{t("IbanModal.ForceValidation")}</span>
                                    </div>
                                </div>
                                <IosSwitch {...field} checked={field.value} id="bypassIbanValidation" />
                            </FormGroup>
                        )}
                    />
                </div>
            )}
            <div className="iban-modal-btns">
                <StyledButton onClick={() => close()} fullWidth sx={{ ...whiteButton, color: "#ED6E6E" }}>
                    {t("IbanModal.IbanFormCancel")}
                </StyledButton>
                <StyledButton buttonType="primary" fullWidth onClick={handleSubmit(onSubmit)} loading={isLoading}>
                    {t("IbanModal.IbanFormSave")}
                </StyledButton>
            </div>
        </div>
    );
};

export default IbanModal;
